import { useState } from 'react';
import styled from 'styled-components';
import {
  StatusIndicator,
  TableCell,
  TableRow,
  Type,
  cssColorValue,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { ProfileProtection } from '@hooks/useGetProfileProtectionData';
import { useProfileProtectionsCopy } from '@hooks/useProfileProtectionsCopy';
import { approvalStatusIndicatorColor } from '@components/shared/ContributorTableRow/approvalStatusColor';
import { useApprovalStatusLabel } from '@components/shared/ContributorTableRow/useApprovalStatusLabel';
import { CopyReleaseId } from '@components/shared/CopyReleaseId';
import { CopyToGreenCheckAnimation } from '@components/shared/CopyToGreenCheckAnimation';
import { NavigationLink } from '@components/shared/ResultsTable/NavigationLink';
import { ExplicitTag } from '@components/shared/styles';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';

const StyledTableRow = styled(TableRow)`
  cursor: pointer;
  height: 1px;
`;

const StyledTableCell = styled(TableCell)`
  vertical-align: middle;
  height: inherit;
`;

const CopyReleaseIdContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
`;

const ProductDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitleAndExplicitContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${cssColorValue('textBase')};
`;

interface ProfileProtectionsTableRowProps {
  row: ProfileProtection;
}

export const ProfileProtectionsTableRow = ({
  row,
}: ProfileProtectionsTableRowProps) => {
  const t = useT();
  const [rowIsHovered, setRowHovered] = useState(false);
  const copy = useProfileProtectionsCopy();
  const approvalLabel = useApprovalStatusLabel(row.approval.status);

  const formatMediaType = (mediaType: string): string => {
    switch (mediaType) {
      case 'MEDIA_TYPE_AUDIO_ONLY':
        return t(
          'atlas.media_type.audio',
          'Audio',
          'Text for Audio media type',
        );
      case 'MEDIA_TYPE_VIDEO_ONLY':
        return t(
          'atlas.media_type.video',
          'Video',
          'Text for Video media type',
        );
      case 'MEDIA_TYPE_MIXED':
        return t(
          'atlas.media_type.mixed',
          'Mixed',
          'Label for mixed audio and video media type',
        );
      case 'MEDIA_TYPE_UNKNOWN':
        return t(
          'atlas.media_type.unknown',
          'Unknown',
          'Label for unknown media type',
        );
      default:
        return mediaType;
    }
  };

  const albumUri = row.uri.split(':').pop() ?? '';

  return (
    <StyledTableRow
      hover
      onMouseEnter={() => setRowHovered(true)}
      onMouseLeave={() => setRowHovered(false)}
      onClick={() =>
        sendEvent({
          eventCategory: EVENT_CATEGORY.PROFILE_PROTECTIONS_TABLE,
          eventAction: EVENT_ACTION.CLICK,
          eventLabel: 'Row clicked',
          product: GA_PRODUCT_CATALOG,
        })
      }
    >
      <StyledTableCell>
        <NavigationLink albumUri={albumUri}>
          <Type
            variant="viola"
            semanticColor={rowIsHovered ? 'textBase' : 'textSubdued'}
          >
            {row.feedName}
          </Type>
        </NavigationLink>
      </StyledTableCell>

      <StyledTableCell>
        <NavigationLink albumUri={albumUri}>
          <Type
            variant="viola"
            semanticColor={rowIsHovered ? 'textBase' : 'textSubdued'}
          >
            {row.licensorName}
          </Type>
        </NavigationLink>
      </StyledTableCell>

      <StyledTableCell>
        <NavigationLink albumUri={albumUri}>
          <ProductDetailsContainer>
            <TitleAndExplicitContainer>
              <Type variant="viola" semanticColor="textBase">
                {row.productDetails.title}
              </Type>
              {row.productDetails.explicit && (
                <ExplicitTag aria-label={copy.explicitLyricsAriaLabel} />
              )}
            </TitleAndExplicitContainer>
            <Type variant="viola" semanticColor="textSubdued">
              {row.productDetails.artist}
            </Type>
          </ProductDetailsContainer>
        </NavigationLink>
      </StyledTableCell>

      <StyledTableCell>
        <NavigationLink albumUri={albumUri}>
          <StatusIndicator
            semanticColor={approvalStatusIndicatorColor(row.approval.status)}
          >
            <Type
              variant="viola"
              semanticColor={rowIsHovered ? 'textBase' : 'textSubdued'}
            >
              {approvalLabel}
            </Type>
          </StatusIndicator>
        </NavigationLink>
      </StyledTableCell>

      <StyledTableCell>
        <CopyReleaseIdContainer>
          <CopyReleaseId primaryReleaseId={row.primaryReleaseId} />
        </CopyReleaseIdContainer>
      </StyledTableCell>

      <StyledTableCell>
        <NavigationLink albumUri={albumUri}>
          <Type
            variant="viola"
            semanticColor={rowIsHovered ? 'textBase' : 'textSubdued'}
          >
            {row.productType}
          </Type>
        </NavigationLink>
      </StyledTableCell>

      <StyledTableCell>
        <NavigationLink albumUri={albumUri}>
          <Type
            variant="viola"
            semanticColor={rowIsHovered ? 'textBase' : 'textSubdued'}
          >
            {formatMediaType(row.includedMedia)}
          </Type>
        </NavigationLink>
      </StyledTableCell>

      <StyledTableCell>
        <NavigationLink albumUri={albumUri}>
          <Type
            variant="viola"
            semanticColor={rowIsHovered ? 'textBase' : 'textSubdued'}
          >
            {row.originalReleaseDate}
          </Type>
        </NavigationLink>
      </StyledTableCell>

      <StyledTableCell align="center">
        <CopyToGreenCheckAnimation
          label={copy.copyUri}
          hasHoverBackground
          tooltipText={copy.copyUri}
          screenReaderText={copy.copyUri}
          copyText={row.uri}
          handleClick={() =>
            sendEvent({
              eventCategory: EVENT_CATEGORY.SEARCH_PAGE_PROFILE_PROTECTIONS,
              eventAction: EVENT_ACTION.CLICK,
              eventLabel: 'URI copied',
              product: GA_PRODUCT_CATALOG,
            })
          }
        />
      </StyledTableCell>
    </StyledTableRow>
  );
};
